// src/pages/AssortedDessertBox/AssortedDessertBoxListPage.jsx
import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Container } from "@mui/material";
import api from "../../services/api";
import QuantityAndCart from "../../components/QuantityAndCart";
import SnackbarAlert from "../../components/SnackbarAlert";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import { buildLineId } from "../../utils/lineId";

const AssortedDessertBoxListPage = () => {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const { addToCart, setShowCartPreview } = useCart();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    document.title = "Assorted Dessert Boxes";
  }, []);

  useEffect(() => {
    const fetchBoxes = async () => {
      try {
        const res = await api.get("/assorted-dessert-boxes");
        setBoxes(res.data || []);

        const initQuantities = {};
        (res.data || []).forEach((box) => {
          initQuantities[box._id] = box.defaultQuantity || 1;
        });
        setQuantities(initQuantities);

        // FB + GA list impressions
        if (window.fbq && res.data?.length) {
          res.data.forEach((box) => {
            window.fbq("track", "ViewContent", {
              content_name: box.name,
              content_ids: [box._id],
              content_type: "product",
              value: box.basePrice,
              currency: "CAD",
            });
          });
        }
        if (window.gtag && res.data?.length) {
          window.gtag("event", "view_item_list", {
            item_list_id: "assorted_dessert_boxes",
            item_list_name: "Assorted Dessert Boxes",
            items: res.data.map((box) => ({
              item_id: box._id,
              item_name: box.name,
              price: box.basePrice,
              item_category: "Assorted Dessert Box",
            })),
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBoxes();
  }, []);

  const handleAddToCart = async (box) => {
    const quantity = quantities[box._id] || 1;

    // Stable identity
    const type = "assortedDessertBox";
    const lineId = buildLineId({
      productId: box._id,
      type,
      options: { variant: box.subcategory || "general" }, // minimal, deterministic
    });

    const newItem = {
      // identity
      lineId,
      productId: box._id,
      type,
      category: "assortedDessertBox",
      subcategory: box.subcategory || "general",

      // display/meta
      name: box.name,
      displayName: box.name,
      image: box.image || null,

      // options (none selectable here)
      size: null,
      flavor: null,
      color: null,
      customizations: [],
      customization: [],

      // pricing (list vs unit)
      listPrice: Number(box.basePrice || 0),
      unitPrice: Number(box.basePrice || 0),
      quantity,
      totalPrice: Number(box.basePrice || 0) * quantity,

      // step/increments if you have them
      itemsPerSet: Number(box.itemsPerSet || 1),
    };

    const result = await addToCart(newItem);

    // UI feedback
    if (result.ok && result.synced) {
      setSnack({ open: true, msg: "Added to Cart", severity: "success" });
    } else if (result.ok && !result.synced) {
      setSnack({
        open: true,
        msg: "Added to Cart (sync failed, will retry)",
        severity: "warning",
      });
    } else {
      setSnack({
        open: true,
        msg: "Failed to add item. Try again.",
        severity: "warning",
      });
    }
    setShowCartPreview(true);

    // Analytics
    const value = Number(box.basePrice || 0) * quantity;
    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_name: box.name,
        content_ids: [box._id],
        content_type: "product",
        value,
        currency: "CAD",
      });
    }
    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: "CAD",
        value,
        items: [
          {
            item_id: box._id,
            item_name: box.name,
            price: Number(box.basePrice || 0),
            quantity,
            item_category: "assortedDessertBox",
            item_variant: box.subcategory || "general",
          },
        ],
      });
    }

    // No direct api.post here — CartContext handles sync
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Container maxWidth="xl" disableGutters sx={{ py: 4 }}>
      {boxes.length === 0 ? (
        <Box
          sx={{
            mt: 6,
            border: "1px dashed #ccc",
            backgroundColor: "#fafafa",
            borderRadius: 4,
            p: 4,
            textAlign: "center",
            maxWidth: 600,
            mx: "auto",
          }}>
          <SentimentDissatisfiedIcon
            sx={{ fontSize: 50, color: "#999", mb: 1 }}
          />
          <Typography variant="h6" fontWeight="medium" gutterBottom>
            No Assorted Dessert Boxes Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We’re currently updating our selection of dessert boxes. Please
            check back soon or explore our other treats!
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={4}>
          <Typography
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{ px: 2 }}>
            Assorted Dessert Boxes
          </Typography>
          {boxes.map((box) => (
            <Grid item xs={12} md={3} key={box._id}>
              <Box
                sx={{
                  border: "1px solid #eee",
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  maxWidth: 300,
                  margin: "0 auto",
                }}>
                <img
                  src={box.image}
                  alt={box.name}
                  style={{
                    width: "100%",
                    height: 300,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />

                <Typography variant="subtitle1" fontWeight="bold">
                  {box.name}
                </Typography>

                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight="bold">
                  ${Number(box.basePrice || 0).toFixed(2)}
                </Typography>

                {box.description ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {box.description}
                  </Typography>
                ) : null}

                {Array.isArray(box.includedProducts) &&
                  box.includedProducts.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight="bold">
                        What’s included:
                      </Typography>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "1.2em",
                          fontSize: "0.85rem",
                        }}>
                        {box.includedProducts.map((item) => (
                          <li key={item.productId._id}>
                            {item.productId.name}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  )}

                <QuantityAndCart
                  quantity={quantities[box._id]}
                  setQuantity={(val) =>
                    setQuantities((prev) => ({
                      ...prev,
                      [box._id]: val,
                    }))
                  }
                  itemsPerSet={Number(box.itemsPerSet || 1)}
                  onAddToCart={() => handleAddToCart(box)}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <SnackbarAlert
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        severity={snack.severity}
        message={snack.msg}
        bgColor={snack.severity === "success" ? "#4caf50" : "#ffa000"}
        iconColor="#fff"
      />
    </Container>
  );
};

export default AssortedDessertBoxListPage;
